<template>
  <div class="theme-page" :class="{ 'with-assist': isAssistPanelVisible && showThemeAssist }">
    <div class="theme-page__main">
      <div class="theme-page__main-scroll">
        <theme-editor :novelId="novelId" />
      </div>
      <!--
        企画 Assist FAB（GENERATE_THEME / FEEDBACK_THEME / MATCHING_THEME 共通）
        NOTE:
        - wantsCommercialization では出し分けしない（後続レコメンド用。設計書 §10.2）
        - 表示はモバイル以外 × アシスタント猫 FAB 設定（isShowAssistFab）に連動する（意図的。
          step1 の「常時表示」から変更。原稿画面で FAB OFF のユーザーは企画でも非表示）
        - パネル幅のリサイズに追従するよう、本文カラム内に absolute 配置する
      -->
      <assist-fab
        v-if="showThemeAssist"
        class="theme-page__assist-fab"
        size="large"
        @click="toggleAssistPanel"
      />
    </div>
    <assist-panel v-if="showThemeAssist" class="assist-panel" context="theme" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "lodash";
import ThemeEditor from "@/components/organisms/ThemeEditor.vue";
import AssistPanel from "@/components/organisms/AssistPanel.vue";
import AssistFab from "@/components/atoms/AssistFab.vue";
import { isMobile } from "@/constants/breakpoints";
import { ThemeMatchingAnalytics } from "@/lib/utils/analytics/theme";
import { logWarn } from "@/lib/utils/logger";
import { isScriptModeNovel } from "@/lib/models/novel";
import type { Novel } from "@/lib/models/novel";
import {
  isThemeGenerateRedirectRequested,
  removeThemeGenerateRedirectQuery,
} from "@/lib/utils/themeGenerateRedirect";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { ThemeEditor, AssistPanel, AssistFab },
  props: ["novelId"],
  data() {
    return {
      isMobileScreen: isMobile(),
      debouncedOnResize: null as (() => void) | null,
      themeMatchingAnalytics: null as ThemeMatchingAnalytics | null,
      hasTrackedThemeMatchingView: false,
    };
  },
  computed: {
    isAssistPanelVisible(): boolean {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },
    isShowAssistFab(): boolean {
      return this.$store.getters["manuscriptModule/isShowAssistFab"] === true;
    },
    /**
     * 企画 Assist（FAB / パネル）を出すか。
     * - wantsCommercialization は見ない（後続レコメンド切り分け用）
     * - isShowAssistFab には連動する（設計書 §10.2。step1 の常時表示から意図的に変更）
     */
    showThemeAssist(): boolean {
      return !this.isMobileScreen && this.isShowAssistFab;
    },
    // 作品データ（直アクセス時は SideMenu の novelModule/initialize 完了まで undefined）
    currentNovel(): Pick<Novel, "manuscriptMode"> | undefined {
      return this.$store.getters["novelModule/novel"](this.novelId);
    },
  },
  watch: {
    showThemeAssist(enabled: boolean) {
      // 非表示条件になったらパネルを閉じる（空状態を出さない）
      if (!enabled && this.isAssistPanelVisible) {
        this.$store.dispatch("assistNavigationModule/hidePanel");
      }
      if (enabled) {
        this.trackThemeMatchingViewOnce();
      } else {
        this.hasTrackedThemeMatchingView = false;
        // AssistPanel がマウントされない間はクエリ消費主体がいないため、ここで除去する。
        // 初期 false（IndexedDB 読込前）では watcher は走らない。読込後に OFF のままなら mounted 側で除去する。
        this.removeUnusedThemeGenerateRedirectQuery();
      }
    },
    novelId() {
      this.hasTrackedThemeMatchingView = false;
      if (this.showThemeAssist) {
        this.trackThemeMatchingViewOnce();
      }
    },
    // 直アクセス・リロード時、作品データがストアに入るまで currentNovel は undefined。
    // 入った時点で脚本モード判定が確定するため、ここで再評価する。
    currentNovel(novel) {
      if (novel && this.showThemeAssist) {
        this.trackThemeMatchingViewOnce();
      }
    },
  },
  mounted() {
    // アシスタント猫 FAB 表示設定を IndexedDB から読む（原稿画面を経由しない直アクセス対策）
    // 読込前は isShowAssistFab が false のため、完了後にパネル非表示ならクエリを除去する
    this.$store.dispatch("manuscriptModule/initializeGeneralSettings").finally(() => {
      if (!this.showThemeAssist) {
        this.removeUnusedThemeGenerateRedirectQuery();
      }
    });

    if (this.$gtm) {
      this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
    }
    this.$nextTick(() => {
      if (this.showThemeAssist) {
        this.trackThemeMatchingViewOnce();
      }
    });

    this.debouncedOnResize = debounce(() => {
      this.isMobileScreen = isMobile();
    }, 150);
    window.addEventListener("resize", this.debouncedOnResize);
  },
  beforeDestroy() {
    if (this.debouncedOnResize) {
      window.removeEventListener("resize", this.debouncedOnResize);
    }
  },
  methods: {
    trackThemeMatchingViewOnce(): void {
      if (this.hasTrackedThemeMatchingView || !this.showThemeAssist) return;
      // 作品データ取得前は脚本モードかどうか判定できないため、取得できるまで送信を保留する
      // （currentNovel の watcher が取得完了後に再度呼び出す）
      if (!this.currentNovel) return;
      if (isScriptModeNovel(this.currentNovel)) return;
      if (!this.themeMatchingAnalytics && this.$gtm) {
        this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
      }
      this.themeMatchingAnalytics?.trackView({ novelId: this.novelId });
      this.hasTrackedThemeMatchingView = true;
    },
    /**
     * AssistPanel がマウントされない条件では ?assist=generate-theme の消費主体がいない。
     * 共有・ブックマーク URL にクエリが残り続けないよう除去する。
     */
    async removeUnusedThemeGenerateRedirectQuery(): Promise<void> {
      if (this.showThemeAssist) return;
      if (!isThemeGenerateRedirectRequested(this.$route?.query)) return;

      try {
        await this.$router.replace({
          path: this.$route.path,
          query: removeThemeGenerateRedirectQuery(this.$route.query),
          hash: this.$route.hash,
        });
      } catch (error) {
        logWarn(
          { module: "ThemePage", action: "removeUnusedThemeGenerateRedirectQuery" },
          "企画項目生成の遷移パラメータ削除に失敗しました",
          error
        );
      }
    },
    toggleAssistPanel(): void {
      if (!this.showThemeAssist) return;
      if (this.isAssistPanelVisible) {
        this.$store.dispatch("assistNavigationModule/hidePanel");
      } else {
        this.$store.dispatch("assistNavigationModule/showPanel");
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.theme-page {
  display: grid;
  grid-template-columns: 1fr;
  /* auto 行だとパネルが内容高さになり、内側 overflow-y が効かず縦スクロール不能になる */
  grid-template-rows: minmax(0, 1fr);
  /* 親 (.main / .height-full) の高さに合わせ、ページ全体スクロールにしない */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;

  /* アシスト展開時は本文カラムを圧縮してパネル分の列を確保（パネル幅に追従） */
  &.with-assist {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .assist-panel {
    box-sizing: border-box;
    border-left: 1px solid var(--color-border);
    /* 原稿画面と同様、親グリッドの高さに stretch。AssistPanel 本体は height: 100% */
    align-self: stretch;
    min-height: 0;
    overflow: hidden;
  }
}

.theme-page__main {
  min-width: 0;
  min-height: 0;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.theme-page__main-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.theme-page__assist-fab {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 10;
}
</style>
